/**
 * OpenSearch index naming helpers.
 *
 * Full names: lowercase [a-z0-9._-], start with letter/digit, max 255 bytes.
 * Short names (workspace suffix after `ws-{id}-`): matches backend
 * BeasOpenSearchNaming.SHORT_NAME — lowercase [a-z0-9_-], 1–64 chars, start with letter/digit.
 */

const INDEX_NAME = /^[a-z0-9][a-z0-9._-]*$/;
/** Backend BeasOpenSearchNaming.assertValidShortName */
const SHORT_NAME = /^[a-z0-9][a-z0-9_-]{0,63}$/;

export type OpenSearchIndexNameError =
  | "empty"
  | "tooLong"
  | "uppercase"
  | "invalidChars"
  | "reserved";

export function validateOpenSearchIndexName(raw: string): {
  ok: boolean;
  error?: OpenSearchIndexNameError;
} {
  const name = raw.trim();
  if (!name) return { ok: false, error: "empty" };
  if (new TextEncoder().encode(name).length > 255) return { ok: false, error: "tooLong" };
  if (name === "." || name === "..") return { ok: false, error: "reserved" };
  if (/[A-Z]/.test(name)) return { ok: false, error: "uppercase" };
  if (!INDEX_NAME.test(name)) return { ok: false, error: "invalidChars" };
  return { ok: true };
}

export function validateOpenSearchIndexShortName(raw: string): {
  ok: boolean;
  error?: OpenSearchIndexNameError;
} {
  const name = raw.trim();
  if (!name) return { ok: false, error: "empty" };
  if (/[A-Z]/.test(name)) return { ok: false, error: "uppercase" };
  if (name.length > 64) return { ok: false, error: "tooLong" };
  if (!SHORT_NAME.test(name)) return { ok: false, error: "invalidChars" };
  return { ok: true };
}

export function isValidOpenSearchIndexName(raw: string): boolean {
  return validateOpenSearchIndexName(raw).ok;
}

export function isValidOpenSearchIndexShortName(raw: string): boolean {
  return validateOpenSearchIndexShortName(raw).ok;
}

/** Join workspace prefix + short name; if name already starts with prefix, return as-is. */
export function resolveWorkspaceIndexName(shortOrFull: string, prefix?: string | null): string {
  const trimmed = shortOrFull.trim().toLowerCase();
  if (!trimmed) return trimmed;
  if (!prefix) return trimmed;
  const p = prefix.toLowerCase();
  if (trimmed.startsWith(p)) return trimmed;
  return `${p}${trimmed}`;
}

/** Matches backend BeasOpenSearchNaming.indexPrefix(workspaceId). */
export function workspaceIndexPrefix(workspaceId: string | null | undefined): string | null {
  const id = workspaceId?.trim();
  if (!id) return null;
  return `ws-${id.toLowerCase()}-`;
}
